import { cn } from "@workspace/web-ui/lib/utils";
import { Calendar, CircleCheck, CircleX, Clock, Info, LucideIcon, Pill } from "lucide-react";


// import { EncounterTagStatusType, TAG_STYLES } from "@workspace/web-ui/lib/encounter";
import { FormattedEncounter } from "../types";
import { ConsultationStatus } from "../../quick-actions/components/consultation-status";
import { Image } from "@workspace/web-ui/components/image";
// import { TAG_STYLES } from "@workspace/web-ui/lib/encounter";

interface UrgentCareEncounterCardProps {
  item: FormattedEncounter;
  selectedEncounter?: FormattedEncounter | null
  setSelectedEncounter?: (state: FormattedEncounter) => void;
  onOpenChange?: (value: boolean) => void;
  program?: string
}

const CARD_COLORS = {
  medication: "bg-[#00C781]",
  appointment: "bg-[#4272DD]",
  consultation: "bg-[#7B238D]"
} as const;

type InfoRowProps = { Icon: LucideIcon | string, alt: string, value: string, testId: string, style?: string, isHappening?: string | null, isRefill?: boolean, age?: number, }

const InfoRow = ({ Icon, alt, value, testId, style }: InfoRowProps) => (
  <div className={cn("flex flex-row gap-2 items-center rounded-[10px] px-[6px] py-[4px] w-fit", style)} data-testid={testId}>
    {typeof Icon === "string" ? (
      <Image className={cn("w-3 h-3 sm:w-4 sm:h-4 text-[#7F8493]")} src={Icon} aria-label={alt} layout="constrained" width={16} height={16} />
    ) : (
      <Icon className={cn("w-3 h-3 sm:w-4 sm:h-4 text-[#7F8493] font-bold")} aria-label={alt} />
    )}
    <span className={cn("text-xs sm:text-sm font-normal text-[#7F8493] whitespace-normal break-words")}>{value}</span>
  </div>
);

const EncounterAppointmentsCard = (props: UrgentCareEncounterCardProps) => {
  const { date } = props.item;

  // const timeAgo = formatTimeAgo(props.item.instanceCreated);

  const diseaseName =
    Array.isArray(props.item.session?.diagnosis) && props.item.session?.diagnosis.length > 0
      ? props.item.session?.diagnosis[0]?.disease_name ?? "No disease name"
      : "No diagnosis available";



  const age = props.item.patient?.age;
  const gender = props.item.patient?.gender;

  const meetingInfo = [
    { Icon: Clock, alt: "clock", value: date || "No time specified", testId: "encounter-date" },
  ];

  return (
    <div className="flex flex-col gap-2">
      {meetingInfo.map((info) => (
        <InfoRow key={info.testId} {...info} />
      ))}
      <div className="flex flex-col gap-2">
        <div className="flex items-center text-sm font-normal text-[#7F8493] gap-2 flex-wrap">
          {age !== undefined && (
            <>
              <span>{age} years old</span>
            </>
          )}
          {gender && (
            <>
              <span className="mx-2">•</span>
              <span>{gender}</span>
            </>
          )}
        </div>
        <p className="text-sm font-normal text-[#7F8493]">
          <span className="font-semibold">Diagnosis:</span> {diseaseName}
        </p>
      </div>

    </div>
  );
};

const EncounterRefillsCard = (props: UrgentCareEncounterCardProps) => {
  const { date, medicalRefills } = props.item;

  const meetingInfo = [
    {
      Icon: Calendar,
      alt: "meeting-date",
      value: date || "No time specified",
      testId: "meeting-date"
    },
    ...(medicalRefills?.map((refill, index) => ({
      Icon: Pill,
      alt: "pill",
      value: `${refill.name} ${refill.dosage}`,
      testId: `pill-name-${index}`
    })) ?? [])
  ];

  return (
    <div className="flex flex-col gap-2 h-fit">
      {meetingInfo.map((info) => (
        <InfoRow key={info.testId} {...info} />
      ))}
    </div>
  );
};

export function formatTimeAgo(dateString: any): any {
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000); // seconds

  if (diff < 60) {
    return diff <= 1 ? "just now" : `${diff} seconds ago`;
  }

  const minutes = Math.floor(diff / 60);
  if (minutes < 60) {
    return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  }

  const days = Math.floor(hours / 24);
  if (days < 7) {
    return days === 1 ? "yesterday" : `${days} days ago`;
  }

  const weeks = Math.floor(days / 7);
  if (weeks < 4) {
    return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return months === 1 ? "1 month ago" : `${months} months ago`;
  }

  const years = Math.floor(days / 365);
  return years === 1 ? "1 year ago" : `${years} years ago`;
}



const EncounterConsultationsCard = (props: UrgentCareEncounterCardProps) => {
  const { item } = props;


  const timeAgo = formatTimeAgo(item.instanceCreated);

  const diseaseName =
    Array.isArray(item.session?.diagnosis) && item.session?.diagnosis.length > 0
      ? item.session?.diagnosis[0]?.disease_name ?? "No disease name"
      : "No diagnosis available";



  const age = item.patient?.age;
  const gender = item.patient?.gender;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center text-sm font-normal text-[#7F8493] gap-2 flex-wrap">
        <span>{timeAgo}</span>
        {age !== undefined && (
          <>
            <span className="mx-2">•</span>
            <span>{age} years old</span>
          </>
        )}
        {gender && (
          <>
            <span className="mx-2">•</span>
            <span>{gender}</span>
          </>
        )}
      </div>
      <p className="text-sm font-normal text-[#7F8493]">
        <span className="font-semibold">Diagnosis:</span> {diseaseName}
      </p>
    </div>
  );
};

export const UrgentCareEncounterCard = (props: UrgentCareEncounterCardProps) => {
  const { selectedEncounter, setSelectedEncounter, onOpenChange, item } = props
  const { id, title, type } = item;

  const cardColor = type === "appointment" ? CARD_COLORS.appointment : type === "consultation" ? CARD_COLORS.consultation : CARD_COLORS.medication;
  const cardsText = title;

  const handleClick = () => {
    if (setSelectedEncounter && item) {
      setSelectedEncounter(item);
      onOpenChange?.(true);
    }
  };

  const isSelected = type === "appointment" ? id === selectedEncounter?.id : type === "refill" ? id === selectedEncounter?.id : type === "consultation" ? id === selectedEncounter?.id : false;

  return (
    <div className="w-full flex h-fit">
      <div
        className={cn("w-[6px] h-auto rounded-tl-[8px] rounded-bl-[8px]", cardColor)}
        role="presentation"
      />
      <div
        className={cn(
          "w-full flex justify-between items-start p-4 border border-[#E6E6E6] rounded-tr-[8px] rounded-br-[8px] shadow-sm bg-white cursor-pointer transition-colors h-fit",
          isSelected && "border-r-[2px] border-t-[2px] border-b-[2px]",
          isSelected && type === "appointment" && "border-[#4272DD]",
          isSelected && type === "refill" && "border-[#00C781]",
          isSelected && type === "consultation" && "border-[#7B238D]",
        )}
        onClick={() => handleClick()}
      >
        <div className="flex flex-col gap-4">
          {type === "consultation" && (
            <ConsultationStatus
              tag={
                selectedEncounter && selectedEncounter.id === item.id
                  ? (Array.isArray(selectedEncounter.recommendedTasks) &&
                    selectedEncounter.recommendedTasks.some(
                      (task) => task.type === "medication_payment" || task.type === "no_action_needed"
                    )
                    ? "Resolved"
                    : "Consultation")
                  : (Array.isArray(item.recommendedTasks) &&
                    item.recommendedTasks.some(
                      (task) => task.type === "medication_payment" || task.type === "no_action_needed"
                    )
                    ? "Resolved"
                    : "Consultation")
              }
              program={props.program}
            />
          )}

          {type === "appointment" && (
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "flex flex-row gap-[2px] items-center rounded-[888px] px-[6px] py-[4px] w-fit border",
                  props.item.tag?.toLowerCase() === "held"
                    ? "text-[#16A34A] bg-[#16A34A26]"
                    : props.item.tag?.toLowerCase() === "not held"
                      ? "text-[#DC2626] bg-[#DC262626]"
                      : "text-[#7F8493] bg-[#EDF0F3]" // grey styling for anything else
                )}
                data-testid="consultation-status"
              >
                <Calendar className={cn(
                  "size-4",
                  props.item.tag?.toLowerCase() === "held"
                    ? "text-[#16A34A]"
                    : props.item.tag?.toLowerCase() === "not held"
                      ? "text-[#DC2626]"
                      : "text-[#7F8493]"
                )} />
                <span className="text-xs font-medium">{props.item.tag || "Not Held"}</span>
              </div>
            </div>
          )}

          {type === "refill" && (
            <div className="flex items-center">
              <div className="border-[#00C78133] text-[#00C781] text-xs font-medium py-[2px] px-[6px] rounded-[888px] border bg-[#E6FAF2] w-fit flex gap-1">
                <Pill className="w-3.5 h-3.5 text-[#00C781]" />
                Refill
              </div>
              {/* Add status tag display for refill request - only show for actual refill statuses */}
              {item?.status &&
                !["past", "upcoming", "happening", "completed", "cancelled", "scheduled", "attended"].includes(item.status) && (
                  <span
                    className={`ml-2 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${item.status === "rejected"
                      ? "bg-red-100 text-red-600 border border-red-200"
                      : item.status === "requested"
                        ? "bg-[#FFF5E6] text-[#FFA726]"
                        : item.status === "fulfilled"
                          ? "bg-blue-100 text-blue-700 border border-blue-200"
                          : item.status === "partially_fulfilled"
                            ? "bg-yellow-100 text-yellow-700 border border-yellow-200"
                            : item.status === "approved"
                              ? "bg-[#E6FAF2] text-[#00C781] border border-green-200"
                              : "bg-gray-100 text-gray-500 border border-gray-200"
                      }`}
                  >
                    {/* icons */}
                    {item.status === "rejected" && <CircleX className="w-3.5 h-3.5 text-[#DC2626]" />}
                    {item.status === "requested" && <Info className="w-3.5 h-3.5 text-[#FFA726]" />}
                    {item.status === "fulfilled" && <CircleCheck className="w-3.5 h-3.5 text-[#00C781]" />}
                    {item.status === "partially_fulfilled" && <CircleCheck className="w-3.5 h-3.5 text-[#00C781]" />}
                    {item.status === "approved" && <CircleCheck className="w-3.5 h-3.5 text-[#00C781]" />}
                    {item.status === "rejected"
                      ? "Rejected"
                      : item.status === "requested"
                        ? "Action Needed"
                        : item.status === "fulfilled"
                          ? "Fulfilled"
                          : item.status === "partially_fulfilled"
                            ? "Partially Fulfilled"
                            : item.status === "approved"
                              ? "Resolved"
                              : item.status?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                  </span>
                )}
            </div>
          )}
          <div className="flex gap-2 md:items-center items-start">
            <h2 className="text-xl font-medium text-[#202020CC] whitespace-normal">{cardsText}</h2>
            {props.program === "urgent-care" ? (
              item.patient.urgent_care_onboarded && item.patient.identity_verified && (
                <div className="">
                  <Image src="/icons/check.svg" alt="timeline" layout="constrained" className="w-4 h-4 sm:w-5 sm:h-5" width={96} height={96} />
                </div>
              )
            ) : (
              item.patient.medical_plan?.payment_status === "paid" && item.patient.glp1_guide_accepted && item.patient.identity_verified && item.patient.supplementary_health_assessment && Object.keys(item.patient.supplementary_health_assessment).length > 0 && (
                <div className="">
                  <Image src="/icons/check.svg" alt="timeline" layout="constrained" className="w-4 h-4 sm:w-5 sm:h-5" width={96} height={96} />
                </div>
              )
            )}
          </div>
          {type === "appointment" && <EncounterAppointmentsCard {...props} />}
          {type === "refill" && <EncounterRefillsCard {...props} />}
          {type === "consultation" && <EncounterConsultationsCard {...props} />}
        </div>
        {item.patient.medical_plan?.payment_status === "paid" && item.patient.glp1_guide_accepted && item.patient.identity_verified && item.patient.supplementary_health_assessment && Object.keys(item.patient.supplementary_health_assessment).length > 0 && (
          <div className="h-6 w-6">
            <Image src="/icons/check.png" alt="timeline" layout="constrained" width={96} height={96} />
          </div>
        )}
      </div>
    </div>
  );
};